/**
 * Props that make a clickable non-button element reachable by keyboard.
 *
 * The book requires that approving payroll with a keyboard be possible and
 * visible, and that every icon-only control carry a label for a screen reader.
 * A bare `<Box onClick>` meets none of that: it takes no focus, fires on no
 * key, and announces as nothing.
 *
 * Spread it in place of the onClick prop:
 *
 *   <Box {...pressable(() => open(row))}>…</Box>
 *   <Box {...pressable(remove, "Remove line")}><DeleteOutlineIcon /></Box>
 *
 * Spreading rather than wrapping in a component is deliberate: it leaves the
 * element, its sx and its children exactly as they were.
 *
 * Enter and Space both activate, matching a native button — Space is the one a
 * screen-reader user reaches for, and dropping it is the usual silent failure.
 * The focus ring itself comes from the global `:focus-visible` rule in
 * index.css, so it stays consistent with every other target in the product.
 */
export const pressable = (onClick, label) => {
  // Call sites pass a conditional handler (`multi ? open : undefined`). With no
  // handler there is nothing to press, so the element must not advertise itself
  // as a button or take a tab stop — a focusable no-op is worse than a plain
  // div, because it promises an action that never comes.
  if (typeof onClick !== "function") return {};

  return {
    role: "button",
    tabIndex: 0,
    ...(label ? { "aria-label": label } : null),
    onClick,
    onKeyDown: (e) => {
      if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
      // Space scrolls the page by default; Enter can submit an enclosing form.
      e.preventDefault();
      onClick(e);
    },
  };
};

export default pressable;
